import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import PageTitle from "../../../components/partials/PageTitle";
import Select from "../../../components/form/select/Select";
import { fetchAllOffices } from "../../../redux/slice/office/officeSlice";
import {
  getAllRoles,
  getAllStaffs,
  resetStaffInformation,
} from "../../../redux/slice/staff/staffSlice";
import { useDispatch, useSelector } from "react-redux";
import {
  getStaffInformation,
  resetStaffStatus,
} from "../../../redux/slice/staff/staffSlice";
import { createUser, resetUsers } from "../../../redux/slice/user/userSlice";
import { useNavigate } from "react-router-dom";
import { BounceLoader } from "react-spinners";
import { flashMessage as flash } from "../../../utils/flash";

const AddUser = () => {
  const [pageNumber, setPageNumber] = useState(1);
  const { user } = useSelector((state) => state.auth?.user);
  const [staffValue, setStaffValue] = useState({
    staffId: "",
    firstname: "",
    lastname: "",
    email: "",
    office_id: "",
  });
  const [selected, setSelected] = useState(false);
  const [firstLogin, setFirstLogin] = useState(false);
  const [staffExists, setStaffExists] = useState(false);
  const [staffIsSelected, setStaffIsSelected] = useState(false);
  const [staffSelect, setStaffSelect] = useState({
    value: "",
  });
  const [rolesSelect, setRolesSelect] = useState([]);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { offices } = useSelector((state) => state.office);
  const { staff_list, roles } = useSelector((state) => state.staff);

  const {
    staff_information,
    isSuccess: staffSuccess,
    isLoading,
  } = useSelector((state) => state?.staff);
  const { isSuccess, createSuccess } = useSelector((state) => state?.user);
  const { user_permissions } = useSelector((state) => state?.auth?.user);

  const breadcrumb = {
    from: {
      title: "User",
      link: "/admin/all-users",
    },
    current: {
      title: "onboarding",
      link: null,
    },
  };

  const onSubmit = (values, { resetForm }) => {
    if (!rolesSelect.length) {
      return flash("error", "Atleast one role is required");
    }
    let payload = {
      ...values,
      roles_arrays: rolesSelect,
      first_login: firstLogin,
    };
    dispatch(createUser(payload));
    dispatch(resetStaffInformation());
    setStaffSelect({ ...staffSelect, value: "" });
    setSelected(false);
    setFirstLogin(false);
  };

  const handleRoleSelect = (e) => {
    let object = { role: e.target.value, checked: e.target.checked };
    if (!object.checked) {
      let sorted = rolesSelect.filter((item) => item.role_name !== object.role);
      setRolesSelect(sorted);
    } else setRolesSelect([...rolesSelect, { role_name: object.role }]);
  };

  const handleStaffName = (values) => {
    dispatch(resetStaffInformation());
    setStaffIsSelected(true);
    setSelected(true);
    setStaffSelect({ ...staffSelect, values });
    dispatch(getStaffInformation(values?.value));
  };

  const validationScehma = Yup.object().shape({
    staff_id: Yup.string("Staff Id must be a string").required(
      "Staff Id cannot be blank"
    ),
    first_name: Yup.string("First Name must be a string").required(
      "First Name cannot be blank"
    ),
    last_name: Yup.string("Last Name must be a string").required(
      "Last Name cannot be blank"
    ),
    email: Yup.string()
      .email("Invalid email address")
      .required("Email cannot be blank"),
    password: Yup.string().required("Password cannot be blank"),
    password_confirmation: Yup.string()
      .required("Confirm password field is required")
      .oneOf([Yup.ref("password")], "Password does not match!"),
    office_id: Yup.string().required("Office is required"),
  });

  const {
    values,
    errors,
    touched,
    handleBlur,
    handleChange,
    handleSubmit,
    setFieldValue,
  } = useFormik({
    enableReinitialize: true,
    initialValues: {
      staff_id: staffValue?.staffId || "",
      first_name: staffValue?.firstname || "",
      last_name: staffValue?.lastname || "",
      email: staffValue?.email || "",
      password: "",
      password_confirmation: "",
      office_id: staffValue?.office_id || "",
    },
    validationSchema: validationScehma,
    onSubmit,
  });

  const officeData = offices?.offices?.map((office) => {
    return {
      label: office.office_name,
      value: office.id,
    };
  });
  const data = staff_list?.data?.map((name) => {
    return {
      label: name.first_name + " " + name.last_name,
      value: name.id,
    };
  });

  useEffect(() => {
    if (!staffExists) {
      setStaffValue({
        firstname: "",
        lastname: "",
        email: "",
        staffId: "",
        office_id: "",
      });
    }
  }, [staffExists, values]);

  useEffect(() => {
    dispatch(
      fetchAllOffices({ office_id: user?.office_id, pageNumber: pageNumber })
    );

    dispatch(getAllStaffs());

    dispatch(getAllRoles());
  }, [dispatch, pageNumber, staff_information, user?.office_id]);

  useEffect(() => {
    if (isSuccess) {
      navigate("/admin/all-users");
      dispatch(resetUsers());
      dispatch(resetStaffInformation());
    }
  }, [dispatch, isSuccess, navigate]);

  useEffect(() => {
    if (staffSuccess && selected) {
      setStaffValue({
        firstname: staff_information?.personal_information?.first_name,
        lastname: staff_information?.personal_information?.last_name,
        email: staff_information?.contact_information?.email,
        staffId: staff_information?.employment_information?.[0]?.staff_id,
        office_id: staff_information?.employment_information?.[0]?.office_id,
      });

      dispatch(resetStaffStatus());
      return;
    }
  }, [
    staffSuccess,
    dispatch,
    staff_information?.personal_information?.first_name,
    staff_information?.personal_information?.last_name,
    staff_information?.contact_information?.email,
    staff_information?.employment_information,
    selected,
  ]);

  useEffect(() => {
    if (
      !user_permissions?.some(
        (permission) => permission.name === "user module"
      ) ||
      !user_permissions?.some((permission) => permission.name === "add user")
    ) {
      navigate("/admin/dashboard");
    }
  }, [navigate, user_permissions]);

  useEffect(() => {
    if (createSuccess) {
      setStaffValue({
        firstname: "",
        lastname: "",
        email: "",
        staffId: "",
        office_id: "",
      });
      setRolesSelect([]);
    }
  }, [createSuccess]);

  return (
    <>
      {/* page title */}
      <PageTitle title='Create User' breadcrumb={breadcrumb} />
      {/* end page title */}

      <div className='row'>
        <div className='col-md-12'>
          <div className='card '>
            {isLoading && staffIsSelected && (
              <div
                className='row h-auto col-12 z-1000 mt-lg-5 justify-content-center align-content-center position-absolute'
                // style={{backgroundColor: "#fffff"}}
              >
                <BounceLoader color='#2E3192' size={"150px"} />
              </div>
            )}
            <form onSubmit={handleSubmit}>
              <div className='card-body position-relative'>
                <div className='flex align-items-center'>
                  <label
                    className='form-label fw-bolder'
                    htmlFor='staff'
                    style={{
                      textAlign: "right",
                      paddingRight: "25px",
                      width: "17%",
                    }}
                  >
                    <p>Existing Staff ?</p>
                  </label>
                  <input
                    className={`form-check-input`}
                    type='checkbox'
                    onChange={() => setStaffExists(!staffExists)}
                    style={{ height: "20px", width: "20px" }}
                    // defaultChecked={rolesSelect.some((item) => item === role)}
                  />
                </div>
                {staffExists && (
                  <div className='row mb-3'>
                    <div className='col-sm-2 text-end sm-text-end'>
                      <label
                        htmlFor='account_type'
                        className={`form-label mt-1`}
                      >
                        Staff Name
                      </label>
                    </div>

                    <div className='col-sm-6'>
                      <Select
                        name='staffname'
                        data={data}
                        value={staffSelect?.value}
                        onChange={(values) => handleStaffName(values)}
                      />
                      {/* <Select name="staffname" data={data} value={values?.staffname} onChange={(selectedOption) => setFieldValue("staffname",selectedOption?.value)} /> */}
                    </div>
                  </div>
                )}
                <div className='row mb-3'>
                  <div className='col-sm-2 text-end sm-text-end'>
                    <label htmlFor='account_type' className={`form-label mt-1`}>
                      Office <span className='text-danger'>*</span>
                    </label>
                  </div>

                  <div className='col-sm-6'>
                    <Select
                      onBlur={() => {
                        handleBlur({ target: { name: "office" } });
                      }}
                      name='office'
                      data={officeData}
                      value={values?.office_id}
                      onChange={(selectedOption) =>
                        setFieldValue("office_id", selectedOption?.value)
                      }
                      disabled={staffValue?.office_id}
                    />
                    {errors?.office && touched?.office && (
                      <p className='mb-0 text-danger'>{errors?.office + "*"}</p>
                    )}
                  </div>
                </div>
                <div className='row mb-3'>
                  <div className='col-sm-2 text-right sm-text-end'>
                    <label for='staff_id' className='col-form-label'>
                      Staff Id
                      <span className='text-danger'> *</span>
                    </label>
                  </div>
                  <div class='col-sm-6'>
                    <input
                      type='text'
                      name='staff_id'
                      className='form-control form-control-sm'
                      id='staff_id'
                      value={values?.staff_id}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={staffValue?.staffId}
                    />
                    {errors?.staff_id && touched?.staff_id && (
                      <p className='mb-0 text-danger'>
                        {errors?.staff_id + "*"}
                      </p>
                    )}
                  </div>
                </div>
                <div className='row mb-3'>
                  <div className='col-sm-2 text-right sm-text-end'>
                    <label htmlFor='first_name' className='col-form-label'>
                      Firstname
                      <span className='text-danger'> *</span>
                    </label>
                  </div>
                  <div class='col-sm-6'>
                    <input
                      type='text'
                      className='form-control form-control-sm'
                      id='first_name'
                      value={values?.first_name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={staffValue?.firstname}
                    />
                    {errors?.first_name && touched?.first_name && (
                      <p className='mb-0 text-danger'>
                        {errors?.first_name + "*"}
                      </p>
                    )}
                  </div>
                </div>
                <div className='row mb-3'>
                  <div className='col-sm-2 text-right sm-text-end'>
                    <label htmlFor='last_name' className='col-form-label'>
                      Lastname
                      <span className='text-danger'> *</span>
                    </label>
                  </div>
                  <div class='col-sm-6'>
                    <input
                      type='text'
                      className='form-control form-control-sm'
                      id='last_name'
                      value={values?.last_name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={staffValue?.lastname}
                    />
                    {errors?.last_name && touched?.last_name && (
                      <p className='mb-0 text-danger'>
                        {errors?.last_name + "*"}
                      </p>
                    )}
                  </div>
                </div>
                <div className='row mb-3'>
                  <div className='col-sm-2 text-right sm-text-end'>
                    <label htmlFor='Email' className='col-form-label'>
                      Email<span className='text-danger'> *</span>
                    </label>
                  </div>
                  <div class='col-sm-6'>
                    <input
                      type='email'
                      className='form-control form-control-sm'
                      id='email'
                      value={values?.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={staffValue?.email}
                    />
                    {errors?.email && touched?.email && (
                      <p className='mb-0 text-danger'>{errors?.email + "*"}</p>
                    )}
                  </div>
                </div>
                <div className='row mb-3'>
                  <div className='col-sm-2 text-right sm-text-end'>
                    <label htmlFor='password' className='col-form-label'>
                      Password
                      <span className='text-danger'> *</span>
                    </label>
                  </div>
                  <div class='col-sm-6'>
                    <input
                      type='password'
                      className='form-control form-control-sm'
                      id='password'
                      value={values?.password}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    {errors?.password && touched?.password && (
                      <p className='mb-0 text-danger'>
                        {errors?.password + "*"}
                      </p>
                    )}
                  </div>
                </div>
                <div className='row mb-1'>
                  <div className='col-sm-2 text-right sm-text-end'>
                    <label
                      htmlFor='password_confirmation'
                      className='col-form-label'
                    >
                      Repeat Password
                      <span className='text-danger'> *</span>
                    </label>
                  </div>
                  <div class='col-sm-6'>
                    <input
                      type='password'
                      className='form-control form-control-sm'
                      id='password_confirmation'
                      value={values?.password_confirmation}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    {errors?.password_confirmation &&
                      touched?.password_confirmation && (
                        <p className='mb-0 text-danger'>
                          {errors?.password_confirmation + "*"}
                        </p>
                      )}
                    <div className='flex align-items-center mt-2'>
                      <input
                        className={`form-check-input`}
                        type='checkbox'
                        id='firstLogin'
                        name='firstLogin'
                        onChange={() => setFirstLogin(!firstLogin)}
                        style={{ height: "20px", width: "20px" }}
                      />
                      <label
                        className='form-label'
                        htmlFor='firstLogin'
                        style={{
                          textAlign: "left",
                          paddingLeft: "15px",
                          width: "65%",
                        }}
                      >
                        <p className=''>
                          Require User to change password on login
                        </p>
                      </label>
                    </div>
                  </div>
                </div>

                <div className='flex align-items-center'>
                  {roles?.roles?.map((role, index) => (
                    <div key={index}>
                      <label
                        className='form-label'
                        htmlFor='superUser'
                        style={{
                          textAlign: "right",
                          paddingRight: "25px",
                          width: "17%",
                        }}
                      >
                        <p>{role}</p>
                      </label>
                      <input
                        className={`form-check-input`}
                        type='checkbox'
                        id={role}
                        value={role}
                        name={role}
                        onChange={handleRoleSelect}
                        style={{ height: "20px", width: "20px" }}
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div className='row mb-3'>
                <div className='col-sm-2'></div>
                <div className='col-sm-6'>
                  <button
                    type='submit'
                    className='btn btn-primary btn-md px-4 rounded'
                  >
                    Create
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddUser;
